/**
 * Echoes.mobi API Service
 *
 * Provides methods to interact with the echoes.mobi public API
 */

import { get } from './client';
import type {
  ItemCategory,
  Item,
  Blueprint,
  ItemBlueprint,
  IndustrySkill,
  CategoriesResponse,
  ItemsResponse,
  BlueprintsResponse,
  ItemBlueprintsResponse,
  IndustrySkillsResponse,
  ItemsQueryParams,
  BlueprintsQueryParams,
  CategoriesQueryParams,
} from '@/types';

// ============================================================================
// Item Categories
// ============================================================================

/**
 * Fetch all item categories
 *
 * @param params - Optional query parameters
 * @returns Array of item categories
 */
export async function fetchCategories(params?: CategoriesQueryParams): Promise<ItemCategory[]> {
  return get<CategoriesResponse>('/item_categories', { params });
}

/**
 * Fetch a specific item category by name
 *
 * @param name - Category name
 * @returns Item category
 */
export async function fetchCategoryByName(name: string): Promise<ItemCategory> {
  return get<ItemCategory>(`/item_categories/${encodeURIComponent(name)}`);
}

// ============================================================================
// Items
// ============================================================================

/**
 * Fetch items with optional pagination and filtering
 *
 * @param params - Query parameters for filtering and pagination
 * @returns Array of items
 */
export async function fetchItems(params?: ItemsQueryParams): Promise<Item[]> {
  return get<ItemsResponse>('/items', { params });
}

/**
 * Fetch all items across all pages
 *
 * This function handles pagination automatically and fetches all items.
 * Warning: This can take a while as there are thousands of items.
 *
 * @param onProgress - Optional callback for progress updates (page, totalFetched)
 * @returns Array of all items
 */
export async function fetchAllItems(
  onProgress?: (page: number, itemsFetched: number) => void
): Promise<Item[]> {
  const allItems: Item[] = [];
  let page = 1;
  let hasMore = true;

  while (hasMore) {
    const items = await fetchItems({ page });

    if (items.length === 0) {
      hasMore = false;
    } else {
      allItems.push(...items);
      onProgress?.(page, allItems.length);
      page++;
    }

    // Add small delay to avoid overwhelming the API
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  return allItems;
}

/**
 * Fetch a specific item by ID
 *
 * @param id - Item ID
 * @returns Item
 */
export async function fetchItemById(id: string): Promise<Item> {
  return get<Item>(`/items/${encodeURIComponent(id)}`);
}

// ============================================================================
// Blueprints
// ============================================================================

/**
 * Fetch blueprints with optional pagination and filtering
 *
 * @param params - Query parameters for filtering and pagination
 * @returns Array of blueprints
 */
export async function fetchBlueprints(params?: BlueprintsQueryParams): Promise<Blueprint[]> {
  return get<BlueprintsResponse>('/blueprints', { params });
}

/**
 * Fetch all blueprints across all pages
 *
 * @param onProgress - Optional callback for progress updates
 * @returns Array of all blueprints
 */
export async function fetchAllBlueprints(
  onProgress?: (page: number, blueprintsFetched: number) => void
): Promise<Blueprint[]> {
  const allBlueprints: Blueprint[] = [];
  let page = 1;
  let hasMore = true;

  while (hasMore) {
    const blueprints = await fetchBlueprints({ page });

    if (blueprints.length === 0) {
      hasMore = false;
    } else {
      allBlueprints.push(...blueprints);
      onProgress?.(page, allBlueprints.length);
      page++;
    }

    // Add small delay
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  return allBlueprints;
}

/**
 * Fetch a specific blueprint by ID
 *
 * @param id - Blueprint ID
 * @returns Blueprint
 */
export async function fetchBlueprintById(id: string): Promise<Blueprint> {
  return get<Blueprint>(`/blueprints/${encodeURIComponent(id)}`);
}

// ============================================================================
// Item Blueprints (v2 API)
// ============================================================================

/**
 * Fetch all item blueprints from v2 API
 *
 * Note: This endpoint returns all blueprints without pagination
 *
 * @returns Array of item blueprints
 */
export async function fetchItemBlueprints(): Promise<ItemBlueprint[]> {
  return get<ItemBlueprintsResponse>('/v2/item_blueprints');
}

/**
 * Find an item blueprint by item ID
 *
 * @param itemId - Item ID to search for
 * @param blueprints - Array of blueprints to search (optional, will fetch if not provided)
 * @returns ItemBlueprint or undefined if not found
 */
export async function findBlueprintByItemId(
  itemId: number,
  blueprints?: ItemBlueprint[]
): Promise<ItemBlueprint | undefined> {
  const allBlueprints = blueprints || (await fetchItemBlueprints());
  return allBlueprints.find((bp) => bp.item_id === itemId);
}

// ============================================================================
// Industry Skills (v2 API)
// ============================================================================

/**
 * Fetch all industry skills from v2 API
 *
 * @returns Array of industry skills with efficiency and time data
 */
export async function fetchIndustrySkills(): Promise<IndustrySkill[]> {
  return get<IndustrySkillsResponse>('/v2/industry_skills');
}

/**
 * Find an industry skill by name
 *
 * @param skillName - Name of the skill
 * @param skills - Array of skills to search (optional, will fetch if not provided)
 * @returns IndustrySkill or undefined if not found
 */
export async function findIndustrySkillByName(
  skillName: string,
  skills?: IndustrySkill[]
): Promise<IndustrySkill | undefined> {
  const allSkills = skills || (await fetchIndustrySkills());
  return allSkills.find((skill) => skill.name === skillName);
}

// ============================================================================
// Item Pricing
// ============================================================================

/**
 * Fetch weekly average price for an item
 *
 * @param itemId - Item ID
 * @returns Weekly average price as a number, or null if not available
 */
export async function fetchItemWeeklyAveragePrice(itemId: number): Promise<number | null> {
  try {
    const response = await get<Array<{ item_id: string; price: string }>>(
      `/item_weekly_average_prices?itemId=${itemId}`
    );
    if (Array.isArray(response) && response.length > 0 && response[0] && response[0].price) {
      const price = parseFloat(response[0].price);
      return isNaN(price) ? null : price;
    }
    return null;
  } catch (error: unknown) {
    // 404s are expected for items without pricing data
    const err = error as { status?: number };
    if (err?.status !== 404 && import.meta.env.DEV) {
      console.warn(`Failed to fetch weekly price for item ${itemId}:`, error);
    }
    return null;
  }
}

/**
 * Fetch current item price (fallback if weekly average not available)
 *
 * @param itemId - Item ID
 * @returns Current item price as a number, or null if not available
 */
export async function fetchItemPrice(itemId: number): Promise<number | null> {
  try {
    // ItemPrice collection returns all prices; we need to filter on our end or use Items API
    // Try getting from Items API instead which has the item with its price
    const response = await get<{ estimated_price?: number | null }>(`/items/${itemId}`);
    if (response.estimated_price !== null && response.estimated_price !== undefined) {
      return response.estimated_price;
    }
    return null;
  } catch (error: unknown) {
    // 404s are expected for items without pricing data
    const err = error as { status?: number };
    if (err?.status !== 404 && import.meta.env.DEV) {
      console.warn(`Failed to fetch price for item ${itemId}:`, error);
    }
    return null;
  }
}

/**
 * Fetch price for an item (tries weekly average first, then falls back to current price)
 * This function also handles caching automatically
 *
 * @param itemId - Item ID
 * @param useCache - Whether to use cached prices (default: true)
 * @returns Price as a number, or null if not available
 */
export async function fetchBestItemPrice(
  itemId: number,
  useCache: boolean = true
): Promise<number | null> {
  // Import apiCache here to avoid circular dependencies
  const { apiCache } = await import('../cache/apiCache');

  // Try cache first if enabled
  if (useCache) {
    const cachedPrice = await apiCache.getCachedPrice(itemId);
    if (cachedPrice !== null) {
      return cachedPrice;
    }
  }

  // Try weekly average first
  const weeklyPrice = await fetchItemWeeklyAveragePrice(itemId);
  if (weeklyPrice !== null) {
    // Cache the price
    if (useCache) {
      await apiCache.setCachedPrice(itemId, weeklyPrice);
    }
    return weeklyPrice;
  }

  // Fallback to current price
  const currentPrice = await fetchItemPrice(itemId);
  if (currentPrice !== null && useCache) {
    // Cache the price
    await apiCache.setCachedPrice(itemId, currentPrice);
  }

  return currentPrice;
}

/**
 * Fetch prices for multiple items in batch
 *
 * @param itemIds - Array of item IDs
 * @returns Map of itemId -> price
 */
export async function fetchItemPricesBatch(itemIds: number[]): Promise<Map<number, number>> {
  const priceMap = new Map<number, number>();

  // Fetch prices in parallel with reasonable batching
  const batchSize = 10;
  for (let i = 0; i < itemIds.length; i += batchSize) {
    const batch = itemIds.slice(i, i + batchSize);
    const promises = batch.map(async (itemId) => {
      const price = await fetchBestItemPrice(itemId);
      return { itemId, price };
    });

    const results = await Promise.all(promises);
    results.forEach(({ itemId, price }) => {
      if (price !== null) {
        priceMap.set(itemId, price);
      }
    });

    // Small delay between batches to avoid overwhelming the API
    if (i + batchSize < itemIds.length) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  return priceMap;
}

// ============================================================================
// Batch Operations
// ============================================================================

/**
 * Fetch all core data needed for the application
 *
 * This is useful for initial cache population
 *
 * @param onProgress - Optional callback for progress updates
 * @returns Object containing all core data
 */
export async function fetchAllCoreData(onProgress?: (step: string, progress: number) => void) {
  onProgress?.('Fetching categories', 0);
  const categories = await fetchCategories();

  onProgress?.('Fetching industry skills', 25);
  const industrySkills = await fetchIndustrySkills();

  onProgress?.('Fetching item blueprints', 50);
  const itemBlueprints = await fetchItemBlueprints();

  onProgress?.('Fetching items', 75);
  const items = await fetchAllItems((page, total) => {
    // Update progress as items are fetched
    // Assuming ~3000 items total, adjust progress accordingly
    const itemProgress = 75 + Math.min(20, (total / 3000) * 20);
    onProgress?.(`Fetching items (page ${page})`, itemProgress);
  });

  onProgress?.('Complete', 100);

  return {
    categories,
    industrySkills,
    itemBlueprints,
    items,
  };
}
